// Edit a listing in place on /creator/listings — drafts and live listings.
//
// Everything saves through POST /api/marketplace/update (the server owns the
// Stripe Product/Price and the database guard freezes money columns for
// browser writes). Only changed fields are sent.
import React, { useRef, useState } from 'react';
import { Image as ImageIcon, Loader, Package, Save, Trash2 } from 'lucide-react';
import { putObject } from '../../utils/uploadToStorage';
import { gathrxrAuthHeader } from '../../utils/quotaHeaders';
import {
  MARKETPLACE_DESCRIPTION_MAX, MARKETPLACE_KINDS, MARKETPLACE_MIN_PRICE_CENTS, MARKETPLACE_TITLE_MAX,
} from '../../config/marketplace';

export interface EditorListing {
  id: string;
  title: string;
  description: string | null;
  kind: string;
  price_cents: number;
  status: string;
  sale_type?: 'copy' | 'original';
  source_id?: string | null;
  thumbnail_url: string | null;
}

const ListingEditor: React.FC<{
  listing: EditorListing;
  onSaved: (msg: string) => void;
  onCancel: () => void;
}> = ({ listing: l, onSaved, onCancel }) => {
  const live = l.status === 'published';
  const [title, setTitle] = useState(l.title);
  const [description, setDescription] = useState(l.description || '');
  const [price, setPrice] = useState((l.price_cents / 100).toFixed(2));
  const [kind, setKind] = useState(l.kind);
  const [saleType, setSaleType] = useState<'copy' | 'original'>(l.sale_type || 'copy');
  const [thumb, setThumb] = useState<string | null>(l.thumbnail_url);
  const [busy, setBusy] = useState<'save' | 'image' | null>(null);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    if (!file.type.startsWith('image/')) return setError('Choose an image file.');
    if (file.size > 8 * 1024 * 1024) return setError('Images up to 8 MB, please.');
    setBusy('image'); setError('');
    try {
      const key = `marketplace/thumbs/${Date.now()}-${file.name.replace(/[^\w.-]+/g, '-')}`;
      setThumb(await putObject(key, file, file.type));
    } catch (e: any) {
      setError(e?.message || 'Image upload failed.');
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setError('');
    const cents = Math.round(parseFloat(price || '0') * 100);
    if (!title.trim()) return setError('Give it a title.');
    if (!Number.isFinite(cents) || cents < MARKETPLACE_MIN_PRICE_CENTS) {
      return setError(`Price must be at least $${(MARKETPLACE_MIN_PRICE_CENTS / 100).toFixed(2)}.`);
    }
    const body: Record<string, unknown> = { listingId: l.id };
    if (title.trim() !== l.title) body.title = title;
    if (description.trim() !== (l.description || '')) body.description = description;
    if (cents !== l.price_cents) body.priceCents = cents;
    if (thumb !== l.thumbnail_url) body.thumbnailUrl = thumb;
    if (!live && kind !== l.kind) body.kind = kind;
    if (!live && saleType !== (l.sale_type || 'copy')) body.saleType = saleType;
    if (Object.keys(body).length === 1) { onCancel(); return; }

    setBusy('save');
    try {
      const headers = await gathrxrAuthHeader();
      const res = await fetch('/api/marketplace/update', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d?.error || 'Could not save.'); return; }
      onSaved(live && body.priceCents !== undefined
        ? `Saved. "${d.listing?.title || title}" now sells for $${(cents / 100).toFixed(2)}.`
        : `Saved "${d.listing?.title || title}".`);
    } catch {
      setError('Could not reach the server.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mk-edit mk-form">
      <div className="mk-edit-cover">
        <span className="mk-row-thumb">{thumb ? <img src={thumb} alt="" /> : <Package size={18} />}</span>
        <input ref={fileRef} type="file" hidden accept="image/*"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        <button type="button" className="mk-ghost" onClick={() => fileRef.current?.click()} disabled={busy !== null}>
          {busy === 'image' ? <Loader size={13} className="spin" /> : <ImageIcon size={13} />}
          {thumb ? 'Replace cover' : 'Add cover'}
        </button>
        {thumb && (
          <button type="button" className="mk-ghost mk-danger" onClick={() => setThumb(null)} title="Remove cover">
            <Trash2 size={13} />
          </button>
        )}
      </div>

      <div className="mk-fields">
        <label>
          <span>Title</span>
          <input value={title} maxLength={MARKETPLACE_TITLE_MAX} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          <span>Type{live ? ' (locked while live)' : ''}</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)} disabled={live}>
            {MARKETPLACE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label>
          <span>Price (USD)</span>
          <input type="number" min={(MARKETPLACE_MIN_PRICE_CENTS / 100).toFixed(2)} step="0.01"
            value={price} onChange={(e) => setPrice(e.target.value)} />
        </label>
      </div>

      <label className="mk-full" style={{ marginTop: 0 }}>
        <span>Description — plain text, line breaks are kept</span>
        <textarea rows={5} value={description} maxLength={MARKETPLACE_DESCRIPTION_MAX}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What it is, what's inside, and what someone would use it for." />
        <span className="mk-edit-count">{description.length} / {MARKETPLACE_DESCRIPTION_MAX}</span>
      </label>

      {!live ? (
        <label className="mk-full" style={{ marginTop: 0 }}>
          <span>How it sells</span>
          <select value={saleType} onChange={(e) => setSaleType(e.target.value as 'copy' | 'original')}>
            <option value="copy">Copies — sell any number</option>
            <option value="original" disabled={!l.source_id}>The original — one sale, ownership moves{!l.source_id ? ' (library items only)' : ''}</option>
          </select>
        </label>
      ) : (
        <p className="mk-edit-note">
          Changes go live as soon as you save. A new price applies to every new checkout,
          and anyone mid-checkout at the old price is asked to start again. To change
          what you're selling or its type, unpublish first.
        </p>
      )}

      {error && <p className="mk-error" style={{ margin: 0 }}>{error}</p>}
      <div className="mk-edit-foot">
        <button type="button" className="mk-ghost" onClick={onCancel} disabled={busy === 'save'}>Cancel</button>
        <button type="button" className="mk-primary" onClick={save} disabled={busy !== null}>
          {busy === 'save' ? <Loader size={14} className="spin" /> : <Save size={14} />}
          Save changes
        </button>
      </div>
    </div>
  );
};

export default ListingEditor;
